import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppointmentCard from '../AppointmentCard';
import s from './s.module.less';
import useGetProviderAppointmentList from 'hooks/useGetProviderAppointmentList';
import { Button, Drawer, message, Spin } from 'antd';
import { Prospect } from 'types/common';
import { getProviderAppointmentListCount } from 'api/inbox';
import { PATH } from 'constants/path';

export interface AppointmentItem {
    status?: string;
    title: string;
    date: number;
    time: number;
    duration: number;
    teleHealthLink: string;
    visitReason: string;
    onView?: () => void;
}

interface AppointmentListProps {
    title: string;
    prospect?: Prospect;
    forceReset?: {};
}

const TABS = [
    { key: 'Upcoming', label: 'Upcoming' },
    { key: 'Past', label: 'Pasted' },
];

const ListContent = ({
    activeTab,
    setActiveTab,
    data,
    onNextPage,
    showMoreButton = true,
    loading,
    upcomingCount = 0,
    pastCount = 0,
}: {
    activeTab: 'Upcoming' | 'Past';
    setActiveTab: (tab: 'Upcoming' | 'Past') => void;
    data: any[];
    onNextPage?: (type: 'Upcoming' | 'Past') => void;
    showMoreButton?: boolean;
    loading?: boolean;
    upcomingCount: number;
    pastCount: number;
}) => {
    return (
        <div className={s.listContentWrapper}>
            <div className={s.tabs}>
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.key as 'Upcoming' | 'Past')}
                        type="button"
                    >
                        {tab.label} {tab.key === 'Upcoming' ? `(${upcomingCount})` : `(${pastCount})`}
                    </button>
                ))}
            </div>
            <div className={s.cards}>
                {data.length === 0 ? (
                    <div className={s.empty}>No appointments</div>
                ) : (
                    data.map((item, idx) => (
                        <AppointmentCard key={idx} {...item} />
                    ))
                )}
            </div>
            {showMoreButton && (
                <div className={s.moreButton}>
                    <Button loading={loading} onClick={() => onNextPage?.(activeTab)}>View more appointments</Button>
                </div>
            )}
        </div>
    );
};

const AppointmentList: React.FC<AppointmentListProps> = ({ title, prospect, forceReset }) => {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'Upcoming' | 'Past'>('Upcoming');
    const [upcomingAppointmentList, setUpcomingAppointmentList] = useState<any[]>([]);
    const [upcomingCurrent, setUpcomingCurrent] = useState(1);
    const [upcomingSize, setUpcomingSize] = useState(500);
    const [pastAppointmentList, setPastAppointmentList] = useState<any[]>([]);
    const [pastCurrent, setPastCurrent] = useState(1);
    const [pastSize, setPastSize] = useState(500);
    const [upcomingCount, setUpcomingCount] = useState(0);
    const [pastCount, setPastCount] = useState(0);
    const clientId = prospect?.id;
    const [countsLoading, setCountsLoading] = useState(false);
    const [showMoreButton, setShowMoreButton] = useState(false);

    const fetchCountsByType = useCallback(async () => {
        if (!clientId) {
            return;
        }

        setCountsLoading(true);
        try {
            const res = await getProviderAppointmentListCount(clientId);

            if (res && !res.error) {
                const data = res?.data?.data || {
                    pastSize: 0,
                    upcomingSize: 0,
                };
                setUpcomingCount(data.upcomingSize);
                setPastCount(data.pastSize);
            } else {
                message.error(res?.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCountsLoading(false);
        }
    }, [clientId]);

    const currentData = useMemo(() => {
        if (activeTab === 'Upcoming') {
            return upcomingAppointmentList;
        }
        return pastAppointmentList;
    }, [activeTab, upcomingAppointmentList, pastAppointmentList]);

    const currentPreviewData = useMemo(() => {
        return currentData.slice(0, 3);
    }, [currentData]);

    const [, loading, fetch] = useGetProviderAppointmentList({
        current: 1,
        size: 500,
        type: activeTab as 'Upcoming' | 'Past',
        clientId,
        autoFetch: false,
    });

    const fetchData = useCallback(async (current: number, size: number, type: 'Upcoming' | 'Past', cid?: number) => {
        const res = await fetch(current, size, type, cid);
        if (type === 'Upcoming') {
            // 先暂时解决重复渲染appointment问题
            setUpcomingAppointmentList(res);
            // setUpcomingAppointmentList((prev) => [...prev, ...res]);
        } else {
            setPastAppointmentList(res);
            // setPastAppointmentList((prev) => [...prev, ...res]);
        }
    }, [fetch]);

    useEffect(() => {
        if (clientId) {
            fetchCountsByType();
        }
    }, [clientId, fetchCountsByType]);

    useEffect(() => {
        fetchData(upcomingCurrent, upcomingSize, 'Upcoming', clientId);
    }, [fetchData, upcomingCurrent, upcomingSize, clientId]);

    useEffect(() => {
        fetchData(pastCurrent, pastSize, 'Past', clientId);
    }, [fetchData, pastCurrent, pastSize, clientId]);

    useEffect(() => {
        if (activeTab === 'Upcoming') {
            setShowMoreButton(upcomingAppointmentList.length < upcomingCount);
        } else {
            setShowMoreButton(pastAppointmentList.length < pastCount);
        }
    }, [activeTab, upcomingAppointmentList, pastAppointmentList, upcomingCount, pastCount]);

    const handleNextPage = useCallback((type: 'Upcoming' | 'Past') => {
        if (type === 'Upcoming') {
            setUpcomingCurrent(upcomingCurrent + 1);
        } else {
            setPastCurrent(pastCurrent + 1);
        }
    }, [upcomingCurrent, pastCurrent]);

    const viewMoreButtonRender = useMemo(() => {
        if (activeTab === 'Upcoming') {
            return upcomingAppointmentList.length > 3;
        }
        return pastAppointmentList.length > 3;
    }, [activeTab, upcomingAppointmentList, pastAppointmentList]);

    return (
        <div className={s.listWrapper}>
            <Spin spinning={countsLoading || loading}>
                <div className={s.title}>
                    <div className={s.titleText}>{title}</div>
                    <span onClick={() => {
                        window.open(`${PATH.DASHBOARD}/${PATH.CALENDAR}`, '_blank');
                    }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8.00033 2.66669V13.3334M13.3337 8.00002L2.66699 8.00002" stroke="#374151" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                </div>
                <ListContent
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    data={currentPreviewData}
                    showMoreButton={false}
                    upcomingCount={upcomingCount}
                    pastCount={pastCount}
                />
                {
                    viewMoreButtonRender && (
                        <div className={s.moreButton}>
                            <Button onClick={() => setOpen(true)}>View more appointments</Button>
                        </div>
                    )
                }
                <Drawer
                    title="Basic Drawer"
                    closable
                    onClose={() => setOpen(false)}
                    open={open}
                >
                    <ListContent
                        loading={loading}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        data={currentData}
                        onNextPage={handleNextPage}
                        upcomingCount={upcomingCount}
                        pastCount={pastCount}
                        showMoreButton={showMoreButton}
                    />
                </Drawer>
            </Spin>
        </div>
    );
};

export default AppointmentList;
